import { fabric } from "fabric"
import { type DrawableMixin } from "../objects/drawable_mixin"
import PathObject from "../objects/path"
import Form from "./base"
import Calc from "@/common/util/calculator"
import pathRounding from "@/signomatic/util/path_rounding"

export default class RotatedRectForm extends Form {
  protected path(width: number, height: number, rounding: number) {
    const commands = [this.pathMoveTo(-(width / 2), 0)]

    commands.push(this.pathLineToAbs(0, -(height / 2)))
    commands.push(this.pathLineToAbs(width / 2, 0))
    commands.push(this.pathLineToAbs(0, height / 2))
    commands.push(this.pathClose())

    const path = pathRounding(commands.join(" "), rounding).join("")

    return this.scaleToSize(new PathObject(path, this.defaultParams), width, height)
  }

  protected snappablePaths(width: number, height: number, rounding: number): [DrawableMixin, DrawableMixin] | null {
    const path = this.path(width, height, rounding) as unknown as fabric.Path

    if (path.path) {
      const pathCmds: any[] = [...path.path]
      const sideCmds = [pathCmds.shift(), pathCmds.shift()]

      pathCmds.pop() // Z
      const restCmds = [["M", sideCmds[1][1], sideCmds[1][2]], ...pathCmds]

      const params: fabric.IObjectOptions = {
        ...this.defaultParams,
        scaleX: path.scaleX,
        scaleY: path.scaleY,
        top: 0,
        left: 0,
      }

      const side = new PathObject(sideCmds, params)
      side.pathOffset = new fabric.Point(0, 0)

      return [side, new PathObject(restCmds, params)]
    }

    return null
  }

  protected get baseRounding() {
    return this.calcRounding()
  }

  protected get cutlineRounding() {
    return this.printCutlineRounding(this.calcRounding())
  }

  private calcRounding() {
    const { widthPx, heightPx, height, fixmethod, material } = this.state
    let radius = Math.min(widthPx, heightPx) / 10

    if (this.signType === "aluminium_sign") {
      radius = Math.min(height / 2, this.drawables.sizeRelative("width", Calc.mmToPx(3), Calc.mmToPx(70)))
    }

    if (
      ["forex_sign", "solid_plastic_sign", "ecoboard"].includes(this.signType) ||
      (this.signType === "double_sided_sign" && material === "plastic")
    ) {
      radius = Math.max(radius, Calc.mmToPx(5))
    }

    if (fixmethod === "military") {
      radius *= 0.8
    }

    return radius
  }

  private interpolateLinear({
    input,
    inputMin,
    inputMax,
    outputMin,
    outputMax,
    grow = true,
  }: {
    input: number
    inputMin: number
    inputMax: number
    outputMin: number
    outputMax: number
    grow?: boolean
  }): number {
    const diff = inputMax - inputMin
    let t = (input - inputMin) / (diff > 0 ? diff : 1)

    if (grow) {
      t = Math.max(0, Math.min(1, t))
    }

    return outputMin + (outputMax - outputMin) * t
  }

  protected cutlineGappedPath(width: number, height: number, rounding: number): DrawableMixin | undefined {
    if (this.isEtchedAluminiumSign()) {
      const commands = this.rotatedRectCutLine(width, height, rounding).join("")

      const path = new PathObject(commands, {
        ...this.defaultParams,
        stroke: "#f08894",
        strokeWidth: this.drawables.cutlineWidth,
        fill: "transparent",
      })

      return path
    }

    return
  }

  // This function calculates the vertical offset
  protected verticalOffset(ratio: number): number {
    const clampedRatio = Math.min(Math.max(ratio, 0.5), 6)

    const minFactor = 0.5
    const maxFactor = 1.35

    const t = (clampedRatio - 1) / 2

    // Exponential interpolation
    const k = 2 // larger k = sharper decline, smaller k = smoother
    const curved = Math.pow(1 - t, k)

    return minFactor + (maxFactor - minFactor) * curved
  }

  // here we calculate the leg offset factor in between maxRounding and minRounding
  protected legOffsetFactor(rounding: number, width: number, height: number, ratio: number): number {
    const minRounding = 9
    const maxRounding = 38
    const minFactor = 0.2
    const maxFactor = 1

    // Basic interpolation
    const base = this.interpolateLinear({
      input: rounding,
      inputMin: minRounding,
      inputMax: maxRounding,
      outputMin: minFactor,
      outputMax: maxFactor,
    })

    // Exception for 100x100
    const is100x100mm = width === 100 && height === 100

    // For some rounding still need a small dipping
    const dip = this.getDip(ratio, rounding, is100x100mm)

    return base - dip
  }

  protected getDip(ratio: number, rounding: number, is100x100mm: boolean): number {
    // Ratios less than 1
    if (ratio === 0.83) return 0
    if (ratio < 1) {
      return this.interpolateLinear({
        input: ratio,
        inputMin: 0.5,
        inputMax: 1,
        outputMin: 0.09,
        outputMax: 0.025,
        grow: false,
      })
    }

    // Ratio exactly 1
    if (is100x100mm) return 0.03
    if (ratio === 1) return 0

    // Ratios greater than 1
    if ((rounding > 30 || (rounding > 15 && rounding < 16)) && ratio > 1 && ratio < 1.4) {
      return 0.04
    }

    if (rounding > 34 && ratio === 1.4) return 0.05
    if (rounding > 37 && ratio === 1.5) return 0.09
    if (rounding > 30 && ratio > 1.5 && ratio < 1.6) return 0.05
    if (rounding > 34 && ratio > 1.7 && ratio < 1.8) return 0.1
    if (rounding > 15 && ratio > 2.7 && ratio < 2.8) return 0.05

    if (rounding < 10 && ratio > 1) return 0.07
    if (rounding >= 10 && rounding < 15 && ratio > 1) return 0.03
    if (rounding > 15 && rounding <= 18 && ratio > 1) return 0.09
    if (rounding > 18 && rounding <= 27 && ratio > 2) return 0.17
    if (rounding > 18 && rounding <= 27 && ratio > 1 && ratio < 2) return 0.03
    if (rounding > 27 && ratio > 1) return 0.13

    return 0
  }

  // This function calc linearly interpolated value in between max calcRounding(37.7953) and min calcRounding(9.4488).
  // It returns a value that increases with rounding, from 3 to 10.
  protected curveXOffset(rounding: number): number {
    const a = (10 - 3) / (37.7953 - 9.4488)
    const b = 3 - a * 9.4488

    return a * rounding + b
  }

  // This function calculates the depth of rounding for top and bottom curves.
  protected curveYOffset(ratio: number, rounding: number): number {
    // Ratios less than 1
    if (ratio < 1) {
      const ratioToFactor: [number, number][] = [
        [0.5, 1.7],
        [0.56, 1.5],
        [0.6, 1.4],
        [0.62, 1.35],
        [0.7, 1.1],
        [0.8, 0.9],
        [0.9, 0.8],
        [1, 0.5],
      ]

      for (let i = 0; i < ratioToFactor.length - 1; i++) {
        const [x1, y1] = ratioToFactor[i]
        const [x2, y2] = ratioToFactor[i + 1]

        if (ratio >= x1 && ratio <= x2) {
          const t = (ratio - x1) / (x2 - x1)
          const factor = y1 + (y2 - y1) * t
          return rounding * factor
        }
      }

      return rounding * 0.5
    }

    // Ratio exactly 1
    if (ratio === 1) {
      return rounding < 19 ? rounding / 2 + 3 : rounding / 2 + 4
    }

    // Ratios slightly above 1
    if (ratio > 1 && ratio < 1.3) return rounding / 2 + 3
    if (ratio === 1.3) return rounding / 2.2
    if (ratio === 1.4) return rounding / 2.4
    if (ratio === 1.5) return rounding / 3
    if (ratio === 1.6 || ratio === 1.7) return rounding / 4 + 2

    // Ratio > 1.7
    return rounding / 4
  }

  // This function calculates the Y offset for the curve and lines that goes before and after curve.
  protected linesYOffset(rounding: number, ratio: number): number {
    // Ratios less than 1
    if (ratio < 1) {
      const ratioToFactor: [number, number][] = [
        [0.5, 1.45],
        [0.56, 1.25],
        [0.6, 1.13],
        [0.62, 1.05],
        [0.7, 1 / 1.15],
        [0.8, 1 / 1.4],
        [0.9, 1 / 1.65],
        [1, 0.45],
      ]

      for (let i = 0; i < ratioToFactor.length - 1; i++) {
        const [x1, y1] = ratioToFactor[i]
        const [x2, y2] = ratioToFactor[i + 1]

        if (ratio >= x1 && ratio <= x2) {
          const t = (ratio - x1) / (x2 - x1)
          const factor = y1 + (y2 - y1) * t
          return rounding * factor
        }
      }

      return rounding * 0.5
    }

    // Ratio exactly 1
    if (ratio === 1) return rounding / 2

    // Ratios > 1
    if (ratio === 1.1) return rounding / 2.2
    if (ratio === 1.2) return rounding / 2.4
    if (ratio === 1.3) return rounding / 2.5 - 2
    if (ratio === 1.4) return rounding / 3
    if (ratio === 1.5) return rounding / 4 + 1
    if (ratio > 1.5 && ratio < 2) return rounding / 4
    if (ratio === 2) return rounding / 4 - 0.5
    if (ratio > 2 && ratio < 2.5) return rounding / 4 - 2
    if (ratio === 3) return rounding / 6

    // All other ratios ≥ 2.5 except 3
    return rounding / 4 - 1
  }

  // This function calculates the Y offset for the last line of first segment.
  protected extraS1LineYOffset(rounding: number, ratio: number): number {
    // Ratios less than 1
    if (ratio < 1) {
      return this.interpolateLinear({
        input: ratio,
        inputMin: 0.5,
        inputMax: 1,
        outputMin: 2,
        outputMax: 1.5,
        grow: false,
      })
    }

    if (ratio === 1) return 1

    // Ratios greater than 1
    if ((rounding > 30 || (rounding > 15 && rounding < 16)) && ratio > 1 && ratio < 1.4) {
      return 0.8
    }

    if (rounding < 10 && ratio > 1) return 0.4
    if (rounding > 37 && ratio === 1.5) return 0.7
    if (rounding > 15 && rounding <= 18 && ratio > 1) return 0.7

    return 0.5
  }

  // This function calculates the Y offset for the last line of second and third segments.
  protected extraS2S3LineYOffset(rounding: number, ratio: number): number {
    // Ratios less than 1
    if (ratio < 1) {
      return this.interpolateLinear({
        input: ratio,
        inputMin: 0.5,
        inputMax: 1,
        outputMin: 2.2,
        outputMax: 1.5,
        grow: false,
      })
    }
    if (ratio === 1) return 1.2

    // Ratios greater than 1
    if ((rounding > 30 || (rounding > 15 && rounding < 16)) && ratio > 1 && ratio < 1.4) {
      return 0.7
    }

    return 0.5
  }

  // This function calculates the Y offset for the small ratio.
  protected smallRatioYOffset(ratio: number): number {
    if (ratio === 0.5 || ratio === 0.6) {
      return 3
    } else if (ratio === 0.83 || ratio >= 1) {
      return 0
    }

    return 1.5
  }

  // This function calculates the Y offset for the line before right curve and right curve. And for the left curve.
  protected additionalOffset(rounding: number, ratio: number): number {
    if (ratio === 1 && rounding > 37) return 3
    if (ratio > 1 && rounding > 18) return 1
    if (ratio > 1 && rounding < 10) return 0
    return 0.5
  }

  protected rotatedRectCutLine(width: number, height: number, rounding: number): string[] {
    const legLength = Calc.mmToPx(2.5)
    const gap = this.gapLength()

    const halfWidth = width / 2
    const halfHeight = height / 2
    const centerX = 0
    const ratio = width / height
    const roundRatio = Math.round(ratio * 100) / 100
    const mmWidth = Calc.pxToMm(width)
    const mmHeight = Calc.pxToMm(height)

    const verticalOffset = rounding * this.verticalOffset(roundRatio)
    const additionalOffset = this.additionalOffset(rounding, roundRatio)
    const legOffset = this.legOffsetFactor(rounding, mmWidth, mmHeight, roundRatio) * legLength
    const linesYOffset = this.linesYOffset(rounding, roundRatio)
    const curveYOffset = this.curveYOffset(roundRatio, rounding)

    // Perpendicular legs
    const legRight = legLength / Math.SQRT2
    const legLeft = -legLength / Math.SQRT2

    // Midpoint positions
    const midX = halfWidth / 2 + legOffset
    const midY = halfHeight / 2 + legOffset

    // Special vertical offsets for specific sizes
    const isSquare100 = roundRatio === 1 && mmWidth === 100
    const isSquare50 = roundRatio === 1 && mmWidth === 50
    const isBigRounding = rounding > 24 && !isSquare100

    const offset100Y = isSquare100 ? 0.5 : 0
    const offset50Y = isSquare50 ? 0.5 : 0
    const bigRoundingY = isBigRounding ? 1 : 0
    const smallRatioYOffset = this.smallRatioYOffset(roundRatio)

    const segments: string[] = []

    // === Segment 1: Top → Right Bottom ===
    const s1 = []
    s1.push(this.pathMoveTo(midX + legRight, -midY - legRight))
    s1.push(this.pathLineToAbs(midX, -midY))
    s1.push(
      this.pathLineToAbs(
        halfWidth - rounding / 4,
        -verticalOffset / 2 - additionalOffset - offset50Y - bigRoundingY - smallRatioYOffset
      )
    )
    s1.push(
      this.pathCurveAbs(
        halfWidth - rounding / 4,
        -verticalOffset / 2 - additionalOffset - offset50Y - bigRoundingY - smallRatioYOffset,
        halfWidth + this.curveXOffset(rounding) + offset100Y * 4 + offset50Y * 2 + bigRoundingY * 2,
        0,
        halfWidth - rounding / 4,
        verticalOffset / 2 + additionalOffset + bigRoundingY + offset50Y + smallRatioYOffset
      )
    )
    s1.push(this.pathLineToAbs(midX + gap, midY - this.extraS1LineYOffset(rounding, roundRatio)))
    segments.push(s1.join(" "))

    // === Segment 2: Bottom to Left-Bottom ===
    const s2 = []
    s2.push(this.pathMoveTo(midX + legRight, midY + legRight))
    s2.push(this.pathLineToAbs(midX, midY))
    s2.push(this.pathLineToAbs(centerX + rounding, halfHeight - linesYOffset))
    s2.push(
      this.pathCurveAbs(
        centerX + rounding,
        halfHeight - linesYOffset,
        centerX + 1.5,
        halfHeight + curveYOffset,
        centerX - rounding,
        halfHeight - linesYOffset
      )
    )
    s2.push(this.pathLineToAbs(-midX + gap, midY + this.extraS2S3LineYOffset(rounding, roundRatio)))
    segments.push(s2.join(" "))

    // === Segment 3: Left to Top ===
    const s3 = []
    s3.push(this.pathMoveTo(-midX + legLeft, midY - legLeft))
    s3.push(this.pathLineToAbs(-midX, midY))
    s3.push(
      this.pathLineToAbs(
        -halfWidth + rounding / 4,
        verticalOffset / 2 + additionalOffset + bigRoundingY + smallRatioYOffset
      )
    )
    s3.push(
      this.pathCurveAbs(
        -halfWidth + rounding / 4,
        verticalOffset / 2 + additionalOffset + bigRoundingY + smallRatioYOffset,
        -halfWidth - this.curveXOffset(rounding) - offset100Y * 4 - offset50Y * 2 - bigRoundingY * 2,
        0,
        -halfWidth + rounding / 4,
        -verticalOffset / 2 - additionalOffset - smallRatioYOffset - bigRoundingY
      )
    )
    s3.push(this.pathLineToAbs(centerX - rounding, -halfHeight + linesYOffset))
    s3.push(
      this.pathCurveAbs(
        centerX - rounding,
        -halfHeight + linesYOffset,
        centerX - 1.5,
        -halfHeight - curveYOffset,
        centerX + rounding,
        -halfHeight + linesYOffset
      )
    )

    s3.push(this.pathLineToAbs(midX - gap, -midY - this.extraS2S3LineYOffset(rounding, roundRatio)))
    segments.push(s3.join(" "))

    return segments
  }
}
